import { WorkspaceImpl } from '@affine/core/modules/workspace/impls/workspace';
import { I18n } from '@affine/i18n';
import { getSurfaceBlock } from '@blocksuite/affine/blocks/surface';
import {
  DatabaseBlockModel,
  EmbedLinkedDocModel,
  EmbedSyncedDocModel,
  FrameBlockModel,
  GroupElementModel,
  ImageBlockModel,
  NoteBlockModel,
  NoteDisplayMode,
} from '@blocksuite/affine/model';
import {
  embedSyncedDocMiddleware,
  MarkdownAdapter,
  titleMiddleware,
} from '@blocksuite/affine/shared/adapters';
import {
  getImageSelectionsCommand,
  getSelectedBlocksCommand,
} from '@blocksuite/affine/shared/commands';
import {
  DocModeProvider,
  UserProvider,
} from '@blocksuite/affine/shared/services';
import {
  getBlockProps,
  isInsideEdgelessEditor,
  matchModels,
} from '@blocksuite/affine/shared/utils';
import { BlockStdScope, type EditorHost } from '@blocksuite/affine/std';
import {
  GfxControllerIdentifier,
  GfxPrimitiveElementModel,
} from '@blocksuite/affine/std/gfx';
import type { BlockModel, DocSnapshot, Store } from '@blocksuite/affine/store';
import { Slice, toDraftModel } from '@blocksuite/affine/store';
import { getElementProps } from '@blocksuite/affine-block-root';
import {
  FILE_INDEX_MANAGED_BY,
  isFileIndexDatabaseId,
} from '@streamlain/database-identity';
import { Doc as YDoc } from 'yjs';

import { getStoreManager } from '../../manager/store';
import type { ChatContextValue } from '../components/ai-chat-content';
import type {
  CanvasAIChatContextMode,
  CanvasSelectionItem,
} from '../components/ai-chat-content/type';
// Import and re-export from screenshot service for backwards compatibility
import {
  getScreenshotService,
  type IScreenshotService,
  type ScreenshotProgressHandler,
  ScreenshotService,
  type ScreenshotSizeCategory,
  type ScreenshotSizeValidation,
  ScreenshotTooLargeError,
} from '../services/screenshot-service';
import { getAIChatSelectionContext } from './ai-chat-selection-context';
import { isAttachment } from './attachment';
import { finiteBoundsFromXYWH } from './canvas-bounds';
import { flattenCanvasSelectionItems } from './canvas-selection-areas';
import { buildCanvasSelectionContextFromItems } from './canvas-selection-context-builder';
import {
  canvasSelectionRoleLabel,
  canvasSelectionTypeLabel,
} from './canvas-selection-display';
import {
  getSelectedAttachments,
  getSelectedImagesAsBlobs,
  getSelectedTextContent,
} from './selection-utils';

function isHiddenFileIndexSelection(element: unknown): boolean {
  const candidate = element as {
    id?: unknown;
    props?: Record<string, unknown>;
  };
  const id = typeof candidate.id === 'string' ? candidate.id : undefined;
  const managedBy = candidate.props?.managedBy;
  const visible = candidate.props?.visible;
  const isFileIndex =
    managedBy === FILE_INDEX_MANAGED_BY || isFileIndexDatabaseId(id);

  return isFileIndex && visible !== true;
}

// Re-export for backwards compatibility
export {
  getScreenshotService,
  type IScreenshotService,
  type ScreenshotProgressHandler,
  ScreenshotService,
  type ScreenshotSizeCategory,
  type ScreenshotSizeValidation,
  ScreenshotTooLargeError,
};

export async function extractSelectedContent(
  host: EditorHost,
  progressHandler?: ScreenshotProgressHandler
): Promise<Partial<ChatContextValue> | null> {
  const docModeService = host.std.get(DocModeProvider);
  const mode = docModeService.getEditorMode() || 'page';
  if (mode === 'edgeless') {
    return await extractEdgelessSelected(host, progressHandler);
  } else {
    return await extractPageSelected(host);
  }
}

function asReadableText(value: unknown) {
  if (typeof value === 'string') {
    const text = value.trim();
    return text.length ? text : null;
  }
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { toString?: unknown }).toString === 'function'
  ) {
    const text = (value as { toString: () => string }).toString().trim();
    if (text && text !== '[object Object]') return text;
  }
  return null;
}

function getCanvasSelectionDirectText(element: unknown) {
  const candidate = element as {
    text?: unknown;
    props?: Record<string, unknown>;
    title?: unknown;
    name?: unknown;
  };
  const values = [
    candidate.text,
    candidate.props?.text,
    candidate.props?.title,
    candidate.props?.name,
    candidate.props?.caption,
    candidate.title,
    candidate.name,
  ];
  for (const value of values) {
    const text = asReadableText(value);
    if (text) return text;
  }
  return null;
}

function getCanvasSelectionDescendantText(
  element: unknown,
  visited = new Set<string>()
): string | null {
  const candidate = element as {
    id?: unknown;
    children?: unknown;
  };
  if (typeof candidate.id === 'string') {
    if (visited.has(candidate.id)) return null;
    visited.add(candidate.id);
  }

  const children = Array.isArray(candidate.children)
    ? candidate.children
    : candidate.children &&
        typeof candidate.children === 'object' &&
        typeof (candidate.children as { values?: unknown }).values ===
          'function'
      ? Array.from(
          (candidate.children as { values: () => Iterable<unknown> }).values()
        )
      : [];

  const parts: string[] = [];
  for (const child of children) {
    const direct = getCanvasSelectionDirectText(child);
    if (direct) parts.push(direct);
    const nested = getCanvasSelectionDescendantText(child, visited);
    if (nested) parts.push(nested);
    if (parts.join(' ').length > 240) break;
  }

  const text = parts.join(' · ').trim();
  return text.length ? text : null;
}

function formatCanvasSelectionSize(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / 1024 / 102.4) / 10} MB`;
}

function getDatabaseTitle(element: unknown): string | null {
  const candidate = element as {
    props?: {
      title?: unknown;
    };
  };
  return asReadableText(candidate.props?.title);
}

function getDatabasePreviewText(element: unknown): string | null {
  const candidate = element as {
    props?: {
      columns?: unknown;
      views?: unknown;
      activeViewId?: unknown;
    };
  };
  const columns = Array.isArray(candidate.props?.columns)
    ? candidate.props.columns.length
    : null;
  const views = Array.isArray(candidate.props?.views)
    ? candidate.props.views
    : [];
  const activeView =
    typeof candidate.props?.activeViewId === 'string'
      ? views.find(
          view =>
            view &&
            typeof view === 'object' &&
            (view as { id?: unknown }).id === candidate.props?.activeViewId
        )
      : views[0];
  const activeViewName =
    activeView && typeof activeView === 'object'
      ? asReadableText((activeView as { name?: unknown; mode?: unknown }).name)
      : null;
  const activeViewMode =
    activeView && typeof activeView === 'object'
      ? asReadableText((activeView as { mode?: unknown }).mode)
      : null;
  const parts = [
    columns === null
      ? null
      : I18n.t('com.streamlain.ai.context.columns', { count: columns }),
    activeViewName ?? activeViewMode,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function getImagePreviewText(element: unknown, label: string): string | null {
  const candidate = element as {
    props?: {
      caption?: unknown;
      width?: unknown;
      height?: unknown;
      size?: unknown;
    };
  };
  const caption = asReadableText(candidate.props?.caption);
  const dimensions =
    typeof candidate.props?.width === 'number' &&
    typeof candidate.props?.height === 'number' &&
    candidate.props.width > 0 &&
    candidate.props.height > 0
      ? `${Math.round(candidate.props.width)} x ${Math.round(
          candidate.props.height
        )} px`
      : null;
  const size = formatCanvasSelectionSize(candidate.props?.size);
  const parts = [
    caption && caption !== label ? caption : null,
    dimensions,
    size,
  ]
    .filter(Boolean)
    .slice(0, 2);
  return parts.length ? parts.join(' · ') : null;
}

function getAttachmentLabel(element: unknown): string | null {
  const candidate = element as {
    props?: {
      name?: unknown;
      caption?: unknown;
    };
  };
  return (
    asReadableText(candidate.props?.name) ??
    asReadableText(candidate.props?.caption)
  );
}

function getAttachmentPreviewText(element: unknown, label: string) {
  const candidate = element as {
    props?: {
      caption?: unknown;
      type?: unknown;
      size?: unknown;
    };
  };
  const caption = asReadableText(candidate.props?.caption);
  const type = asReadableText(candidate.props?.type);
  const size = formatCanvasSelectionSize(candidate.props?.size);
  const parts = [caption && caption !== label ? caption : null, type, size]
    .filter(Boolean)
    .slice(0, 2);
  return parts.length ? parts.join(' · ') : null;
}

function getCanvasSelectionType(element: unknown) {
  const candidate = element as {
    type?: unknown;
    flavour?: unknown;
  };
  if (typeof candidate.type === 'string') return candidate.type;
  if (typeof candidate.flavour === 'string') return candidate.flavour;
  return 'canvas-object';
}

function getCanvasSelectionRole(
  element: unknown
): NonNullable<CanvasSelectionItem['role']> {
  const candidate = element as {
    type?: unknown;
    flavour?: unknown;
    props?: { type?: unknown };
  };
  if (element instanceof GroupElementModel || candidate.type === 'group') {
    return 'group';
  }
  if (
    element instanceof FrameBlockModel ||
    candidate.flavour === 'affine:frame'
  ) {
    return 'frame';
  }
  if (
    element instanceof DatabaseBlockModel ||
    candidate.flavour === 'affine:database'
  ) {
    return 'database';
  }
  if (isAttachment(element as Parameters<typeof isAttachment>[0])) {
    return candidate.props?.type === 'application/pdf' ? 'pdf' : 'attachment';
  }
  if (candidate.type === 'mindmap') return 'mindmap';
  return 'object';
}

function getCanvasSelectionLabel(
  element: unknown,
  role: NonNullable<CanvasSelectionItem['role']>,
  type: string
) {
  const candidate = element as {
    props?: Record<string, unknown> & { name?: unknown; title?: unknown };
    title?: unknown;
    name?: unknown;
  };
  if (type === 'affine:embed-ai-chat') {
    const baseLabel =
      canvasSelectionTypeLabel(type, role) ?? canvasSelectionRoleLabel(role);
    const { labelSuffix } = getAIChatSelectionContext(candidate.props);
    return labelSuffix ? `${baseLabel}: ${labelSuffix}` : baseLabel;
  }
  if (element instanceof NoteBlockModel || type === 'affine:note') {
    return (
      canvasSelectionTypeLabel(type, role) ?? canvasSelectionRoleLabel(role)
    );
  }
  if (
    element instanceof DatabaseBlockModel ||
    role === 'database' ||
    type === 'affine:database'
  ) {
    return (
      getDatabaseTitle(element) ??
      canvasSelectionTypeLabel(type, role) ??
      canvasSelectionRoleLabel(role)
    );
  }
  if (
    isAttachment(element as Parameters<typeof isAttachment>[0]) ||
    role === 'pdf' ||
    role === 'attachment' ||
    type === 'affine:attachment'
  ) {
    return (
      getAttachmentLabel(element) ??
      canvasSelectionTypeLabel(type, role) ??
      canvasSelectionRoleLabel(role)
    );
  }
  const text =
    getCanvasSelectionDirectText(element) ||
    asReadableText(candidate.props?.name) ||
    asReadableText(candidate.props?.title) ||
    asReadableText(candidate.title) ||
    asReadableText(candidate.name);
  if (text) return text;
  const typeLabel = canvasSelectionTypeLabel(type, role);
  if (typeLabel) return typeLabel;
  return canvasSelectionRoleLabel(role);
}

function getCanvasSelectionPreviewText(
  element: unknown,
  role: NonNullable<CanvasSelectionItem['role']>,
  type: string,
  label: string
) {
  if (type === 'affine:embed-ai-chat') {
    const candidate = element as { props?: Record<string, unknown> };
    return getAIChatSelectionContext(candidate.props).transcript;
  }
  if (element instanceof NoteBlockModel || type === 'affine:note') {
    return getCanvasSelectionDescendantText(element);
  }
  if (
    element instanceof DatabaseBlockModel ||
    role === 'database' ||
    type === 'affine:database'
  ) {
    return getDatabasePreviewText(element);
  }
  if (element instanceof ImageBlockModel || type === 'affine:image') {
    return getImagePreviewText(element, label);
  }
  if (type === 'affine:sticky-note') {
    const candidate = element as {
      props?: { text?: unknown; assigneeId?: unknown };
    };
    const stickyText = asReadableText(candidate.props?.text) || label;
    const assigneeId = asReadableText(candidate.props?.assigneeId);
    return assigneeId
      ? `${stickyText}\nAssignee ID: ${assigneeId}`
      : stickyText;
  }
  if (type === 'affine:brush-heading') {
    const candidate = element as { props?: { text?: unknown } };
    return asReadableText(candidate.props?.text) || label;
  }
  if (
    isAttachment(element as Parameters<typeof isAttachment>[0]) ||
    role === 'pdf' ||
    role === 'attachment' ||
    type === 'affine:attachment'
  ) {
    return getAttachmentPreviewText(element, label);
  }

  const direct = getCanvasSelectionDirectText(element);
  if (direct && direct !== label) return direct;
  return canvasSelectionTypeLabel(type, role);
}

function buildCanvasSelectionItem(
  host: EditorHost,
  element: unknown,
  selectedIds: ReadonlySet<string>,
  visited: Set<string>
): CanvasSelectionItem | null {
  const candidate = element as {
    id?: unknown;
    flavour?: unknown;
    group?: { id?: unknown } | null;
    props?: Record<string, unknown>;
    xywh?: unknown;
  };
  if (typeof candidate.id !== 'string' || !candidate.id) return null;
  if (isHiddenFileIndexSelection(element)) return null;
  if (visited.has(candidate.id)) return null;
  visited.add(candidate.id);

  const type = getCanvasSelectionType(element);
  const role = getCanvasSelectionRole(element);
  const label = getCanvasSelectionLabel(element, role, type);
  let text = getCanvasSelectionPreviewText(element, role, type, label);
  const aiChatContext =
    type === 'affine:embed-ai-chat'
      ? getAIChatSelectionContext(candidate.props)
      : null;
  if (type === 'affine:sticky-note') {
    const assigneeId = asReadableText(candidate.props?.assigneeId);
    if (assigneeId) {
      const assignee = host.std
        .getOptional(UserProvider)
        ?.userInfo$(assigneeId).value;
      if (assignee && !assignee.removed && assignee.name) {
        text = `${asReadableText(candidate.props?.text) || label}\nAssignee: ${assignee.name}\nAssignee ID: ${assigneeId}`;
      }
    }
  }
  const bounds = finiteBoundsFromXYWH(candidate.xywh ?? candidate.props?.xywh);
  const kind =
    typeof candidate.flavour === 'string' ? 'block' : 'canvas_element';
  const groupId =
    typeof candidate.group?.id === 'string' ? candidate.group.id : null;
  const parentId = groupId && selectedIds.has(groupId) ? groupId : null;

  return {
    id: candidate.id,
    kind,
    type,
    role,
    label,
    text,
    bounds,
    parentId,
    conversationParentId: aiChatContext?.conversationParentId ?? undefined,
    depth: parentId ? 1 : 0,
    childCount: 0,
    groupId,
  };
}

function normalizeCanvasSelectionItems(
  items: CanvasSelectionItem[]
): CanvasSelectionItem[] {
  const allItems: CanvasSelectionItem[] = [];
  const inheritedParentById = new Map<string, string | null>();
  const collect = (
    selectionItems: CanvasSelectionItem[],
    inheritedParentId: string | null
  ) => {
    for (const item of selectionItems) {
      allItems.push(item);
      inheritedParentById.set(item.id, inheritedParentId);
      if (item.children?.length) collect(item.children, item.id);
    }
  };
  collect(items, null);

  const selectedIds = new Set(allItems.map(item => item.id));
  const parentById = new Map(
    allItems.map(item => {
      const declaredParent = item.parentId;
      return [
        item.id,
        declaredParent && selectedIds.has(declaredParent)
          ? declaredParent
          : (inheritedParentById.get(item.id) ?? null),
      ] as const;
    })
  );
  const childCountById = new Map<string, number>();
  for (const parentId of parentById.values()) {
    if (!parentId) continue;
    childCountById.set(parentId, (childCountById.get(parentId) ?? 0) + 1);
  }
  const depthFor = (itemId: string) => {
    let depth = 0;
    let parentId = parentById.get(itemId) ?? null;
    const seen = new Set([itemId]);
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      depth++;
      parentId = parentById.get(parentId) ?? null;
    }
    return depth;
  };
  const normalize = (item: CanvasSelectionItem): CanvasSelectionItem => {
    const children = item.children?.map(normalize);
    return {
      ...item,
      parentId: parentById.get(item.id) ?? null,
      depth: depthFor(item.id),
      childCount: childCountById.get(item.id) ?? 0,
      children: children?.length ? children : undefined,
    };
  };
  return items.map(normalize);
}

function removeCoveredCanvasSelectionItems(
  items: CanvasSelectionItem[],
  coveredBlockIds: ReadonlySet<string>,
  aiChatContextMode: CanvasAIChatContextMode
): CanvasSelectionItem[] {
  const filtered: CanvasSelectionItem[] = [];

  for (const item of items) {
    // AI chat blocks are conversation lineage, not document context. Keeping
    // them here duplicates the transcript, shows a misleading "AI chat" pill,
    // and can feed the same conversation back into the model as canvas data.
    if (
      item.type === 'affine:embed-ai-chat' &&
      (aiChatContextMode === 'lineage-only' || !item.text)
    ) {
      continue;
    }
    if (item.kind === 'block' && coveredBlockIds.has(item.id)) {
      continue;
    }

    const children = item.children?.length
      ? removeCoveredCanvasSelectionItems(
          item.children,
          coveredBlockIds,
          aiChatContextMode
        )
      : undefined;

    filtered.push({
      ...item,
      children: children?.length ? children : undefined,
      childCount: children?.length ?? 0,
    });
  }

  return normalizeCanvasSelectionItems(filtered);
}

function getCanvasSelectionContextLabel(items: CanvasSelectionItem[]) {
  if (items.length === 1) {
    return items[0].label;
  }

  return I18n.t('com.streamlain.ai.context.canvas.object-many', {
    count: items.length,
  });
}

export function filterCanvasSelectionsByCoveredBlockIds(
  selections: ChatContextValue['canvasSelections'],
  coveredBlockIds: Iterable<string>,
  aiChatContextMode: CanvasAIChatContextMode
): ChatContextValue['canvasSelections'] {
  const covered = new Set([...coveredBlockIds].filter(Boolean));

  return selections
    .map(selection => {
      const items = removeCoveredCanvasSelectionItems(
        selection.items,
        covered,
        aiChatContextMode
      );
      const flatItems = flattenCanvasSelectionItems(items);
      return {
        ...selection,
        items,
        elementIds: flatItems
          .filter(item => item.kind === 'canvas_element')
          .map(item => item.id),
        blockIds: flatItems
          .filter(item => item.kind === 'block')
          .map(item => item.id),
        label: getCanvasSelectionContextLabel(items),
      };
    })
    .filter(selection => selection.items.length > 0);
}

function buildCanvasSelectionContext(
  host: EditorHost,
  selectedElements: unknown[]
): ChatContextValue['canvasSelections'][number] | null {
  const items: CanvasSelectionItem[] = [];
  const visited = new Set<string>();
  const selectedIds = new Set(
    selectedElements.flatMap(element => {
      const id = (element as { id?: unknown }).id;
      return typeof id === 'string' && id ? [id] : [];
    })
  );

  for (const element of selectedElements) {
    const item = buildCanvasSelectionItem(host, element, selectedIds, visited);
    if (item) items.push(item);
  }

  if (!items.length) return null;
  return buildCanvasSelectionContextFromItems({
    docId: host.store.id,
    label: getCanvasSelectionContextLabel(items),
    items,
    source: 'focus',
    priority: 'highest',
  });
}

export function extractCanvasSelectionContext(
  host: EditorHost
): ChatContextValue['canvasSelections'] {
  if (!isInsideEdgelessEditor(host)) return [];
  const gfx = host.std.getOptional(GfxControllerIdentifier);
  if (!gfx) return [];

  const canvasSelection = buildCanvasSelectionContext(
    host,
    gfx.selection.selectedElements
  );
  return canvasSelection ? [canvasSelection] : [];
}

async function extractEdgelessSelected(
  host: EditorHost,
  progressHandler?: ScreenshotProgressHandler
): Promise<Partial<ChatContextValue> | null> {
  if (!isInsideEdgelessEditor(host)) return null;
  const gfx = host.std.get(GfxControllerIdentifier);
  const selectedElements = gfx.selection.selectedElements;
  const canvasSelection = extractCanvasSelectionContext(host)[0] ?? null;

  // Keep the hard bound guard, but do not rasterize canvas selections for chat.
  // The structured canvasSelections payload already carries IDs and labels for
  // follow-up tools without blocking the main thread on html2canvas.
  const screenshotService = getScreenshotService();
  if (progressHandler) {
    screenshotService.setProgressHandler(progressHandler);
  }

  const sizeValidation = screenshotService.validateSize(selectedElements);

  if (sizeValidation.category === 'denied') {
    return canvasSelection
      ? {
          canvasSelections: [canvasSelection],
        }
      : null;
  }

  let snapshot: DocSnapshot | null = null;
  let markdown = '';
  const attachments: ChatContextValue['attachments'] = [];
  const docs: ChatContextValue['docs'] = [];

  if (selectedElements.length) {
    const transformer = host.store.getTransformer();
    const markdownAdapter = new MarkdownAdapter(
      transformer,
      host.store.provider
    );
    const collection = new WorkspaceImpl({
      id: 'AI_EXTRACT',
      rootDoc: new YDoc({ guid: 'AI_EXTRACT' }),
    });
    collection.meta.initialize();

    let needSnapshot = false;
    let needMarkdown = false;
    try {
      const fragmentDoc = collection.createDoc();
      const fragment = fragmentDoc.getStore();
      fragmentDoc.load();

      const rootId = fragment.addBlock('affine:page');
      fragment.addBlock('affine:surface', {}, rootId);
      const noteId = fragment.addBlock('affine:note', {}, rootId);
      const surface = getSurfaceBlock(fragment);
      if (!surface) {
        throw new Error('Failed to get surface block');
      }

      for (const element of selectedElements) {
        if (element instanceof NoteBlockModel) {
          needMarkdown = true;
          for (const child of element.children) {
            const props = getBlockProps(child);
            fragment.addBlock(child.flavour, props, noteId);
          }
        } else if (isAttachment(element)) {
          const { name, sourceId } = element.props;
          if (name && sourceId) {
            attachments.push({ name, sourceId });
          }
        } else if (element instanceof GfxPrimitiveElementModel) {
          needSnapshot = true;
          const props = getElementProps(element, new Map());
          surface.addElement(props);
        } else if (
          element instanceof EmbedSyncedDocModel ||
          element instanceof EmbedLinkedDocModel
        ) {
          const docId = element.props.pageId;
          docs.push(docId);
        }
      }

      if (needSnapshot) {
        snapshot = transformer.docToSnapshot(fragment) ?? null;
      }
      if (needMarkdown) {
        markdown = (await markdownAdapter.fromDoc(fragment))?.file ?? '';
      }
    } finally {
      collection.dispose();
    }
  }

  return {
    snapshot: snapshot ? JSON.stringify(snapshot) : null,
    combinedElementsMarkdown: markdown.length ? markdown : null,
    attachments,
    docs,
    canvasSelections: canvasSelection ? [canvasSelection] : [],
  };
}

async function extractPageSelected(
  host: EditorHost
): Promise<Partial<ChatContextValue> | null> {
  const text = await getSelectedTextContent(host, 'plain-text');
  const images = await getSelectedImagesAsBlobs(host);
  const attachments = await getSelectedAttachments(host);
  const hasText = text.length > 0;
  const hasImages = images.length > 0;

  if (hasText && !hasImages) {
    const markdown = await getSelectedTextContent(host, 'markdown');
    return {
      quote: text,
      markdown: markdown,
      attachments,
    };
  } else if (!hasText && hasImages && images.length === 1) {
    host.command
      .chain()
      .tryAll(chain => [chain.pipe(getImageSelectionsCommand)])
      .pipe(getSelectedBlocksCommand, {
        types: ['image'],
      })
      .run();
    return {
      attachments,
      images,
    };
  } else {
    const markdown =
      (await getSelectedTextContent(host, 'markdown')).trim() || '';
    return {
      quote: text,
      markdown,
      images,
      attachments,
    };
  }
}

export async function extractMarkdownFromDoc(doc: Store): Promise<string> {
  const std = new BlockStdScope({
    store: doc,
    extensions: getStoreManager().config.init().value.get('store'),
  });
  const transformer = await getTransformer(doc);
  const adapter = new MarkdownAdapter(transformer, std.provider);
  const blockModels = getNoteBlockModels(doc);
  const textModels = blockModels.filter(
    model => !matchModels(model, [ImageBlockModel, DatabaseBlockModel])
  );
  const drafts = textModels.map(toDraftModel);
  const slice = Slice.fromModels(doc, drafts);

  const snapshot = transformer.sliceToSnapshot(slice);
  if (!snapshot) {
    throw new Error('Failed to extract markdown, snapshot is undefined');
  }
  const content = await adapter.fromSliceSnapshot({
    snapshot,
    assets: transformer.assetsManager,
  });
  return content.file;
}

function getNoteBlockModels(doc: Store) {
  const notes = doc
    .getBlocksByFlavour('affine:note')
    .filter(
      note =>
        (note.model as NoteBlockModel).props.displayMode !==
        NoteDisplayMode.EdgelessOnly
    )
    .map(note => note.model as NoteBlockModel);
  const blockModels = notes.reduce((acc, note) => {
    acc.push(...note.children);
    return acc;
  }, [] as BlockModel[]);
  return blockModels;
}

async function getTransformer(doc: Store) {
  return doc.getTransformer([
    titleMiddleware(doc.workspace.meta.docMetas),
    embedSyncedDocMiddleware('content'),
  ]);
}
